import React, { useState, useRef, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { fbcStructure, urinalysisTests, testosteroneTests, isFBCTest, isUrinalysisTest, isTestosteroneTest } from '../../store/labStructures';
type ViewingTest = {
  kind: 'lab';
  encounterId: string;
  labId: string;
} | {
  kind: 'rad';
  encounterId: string;
  radId: string;
} | null;
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [viewingTest, setViewingTest] = useState<ViewingTest>(null);
  const [imageZoom, setImageZoom] = useState<number>(100); // percentage 10-200
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const testModalRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      // A4 dimensions in cm
      const pageWidthCm = 21.0;
      const pageHeightCm = 29.7;
      const marginCm = 1.5;
      const contentWidthCm = pageWidthCm - marginCm * 2;
      const contentHeightCm = pageHeightCm - marginCm * 2;
      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      const pdf = new jsPDF({
        unit: 'cm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });
      const imgPropsHeightCm = canvas.height * contentWidthCm / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (imgPropsHeightCm <= contentHeightCm) {
        pdf.addImage(imgData, 'JPEG', marginCm, marginCm, contentWidthCm, imgPropsHeightCm);
      } else {
        let remainingHeightCm = imgPropsHeightCm;
        let positionCm = marginCm;
        while (remainingHeightCm > 0) {
          pdf.addImage(imgData, 'JPEG', marginCm, positionCm, contentWidthCm, imgPropsHeightCm);
          remainingHeightCm -= contentHeightCm;
          if (remainingHeightCm > 0) {
            pdf.addPage();
            positionCm = marginCm - (imgPropsHeightCm - remainingHeightCm);
          }
        }
      }
      pdf.save(filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── Test File modal helpers ───
  const closeTestModal = () => {
    setViewingTest(null);
    setImageZoom(100);
  };
  const handlePrintTestModal = () => {
    // Use a print-only class on body so only the modal prints
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${selectedPatient?.bmcFileNumber || 'patient'}_${Date.now()}.pdf`;
      const pageWidthCm = 21.0;
      const pageHeightCm = 29.7;
      const marginCm = 1.5;
      const contentWidthCm = pageWidthCm - marginCm * 2;
      const contentHeightCm = pageHeightCm - marginCm * 2;
      const canvas = await html2canvas(testModalRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      const pdf = new jsPDF({
        unit: 'cm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });
      const imgPropsHeightCm = canvas.height * contentWidthCm / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (imgPropsHeightCm <= contentHeightCm) {
        pdf.addImage(imgData, 'JPEG', marginCm, marginCm, contentWidthCm, imgPropsHeightCm);
      } else {
        let remainingHeightCm = imgPropsHeightCm;
        let positionCm = marginCm;
        while (remainingHeightCm > 0) {
          pdf.addImage(imgData, 'JPEG', marginCm, positionCm, contentWidthCm, imgPropsHeightCm);
          remainingHeightCm -= contentHeightCm;
          if (remainingHeightCm > 0) {
            pdf.addPage();
            positionCm = marginCm - (imgPropsHeightCm - remainingHeightCm);
          }
        }
      }
      pdf.save(filename);
    } catch (err) {
      console.error('Test PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-119">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-120">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-121">Patient File</h1>
          <div className="flex space-x-2" data-id="element-122">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-123">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-124">
              <Printer className="w-4 h-4 mr-2" data-id="element-125" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-126">
              <Download className="w-4 h-4 mr-2" data-id="element-127" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-128">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-129" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-130">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-131">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-132">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-133">
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-134">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-135">
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-136">
                  <div className="flex items-center" data-id="element-137">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-138" />
                    <div data-id="element-139">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-140">
                        BMED Medical Centre
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-141">
                        Maternity Care Made Better
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-142">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-143">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-144">
                      <span className="font-semibold" data-id="element-145">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-146">
                      <span className="font-semibold" data-id="element-147">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-148">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-149">
                    <div className="col-span-1 font-semibold" data-id="element-150">1.</div>
                    <div className="col-span-6" data-id="element-151">
                      <span className="font-semibold" data-id="element-152">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-153">
                      <span className="font-semibold" data-id="element-154">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-155">
                      <span className="font-semibold" data-id="element-156">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-157">
                    <div className="col-span-1 font-semibold" data-id="element-158">2.</div>
                    <div className="col-span-11" data-id="element-159">
                      <span className="font-semibold" data-id="element-160">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-161">
                    <div className="col-span-1 font-semibold" data-id="element-162">3.</div>
                    <div className="col-span-7" data-id="element-163">
                      <span className="font-semibold" data-id="element-164">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-165">
                      <span className="font-semibold" data-id="element-166">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-167">
                    <div className="col-span-1 font-semibold" data-id="element-168">4.</div>
                    <div className="col-span-11" data-id="element-169">
                      <span className="font-semibold" data-id="element-170">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-171">
                    <div className="col-span-1 font-semibold" data-id="element-172">5.</div>
                    <div className="col-span-5" data-id="element-173">
                      <span className="font-semibold" data-id="element-174">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-175">
                      <span className="font-semibold" data-id="element-176">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-177">
                      <span className="font-semibold" data-id="element-178">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-179">
                    <div className="col-span-1 font-semibold" data-id="element-180">6.</div>
                    <div className="col-span-11" data-id="element-181">
                      <span className="font-semibold" data-id="element-182">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-183">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-184">
                    <div className="col-span-1 font-semibold" data-id="element-185">7.</div>
                    <div className="col-span-11" data-id="element-186">
                      <span className="font-semibold" data-id="element-187">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-188">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-189">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-190">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-191">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-192">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-193">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-194">
                    <thead data-id="element-195">
                      <tr className="bg-gray-50" data-id="element-196">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-197">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-198">
                        <th className="border border-black p-1" data-id="element-199">Temp</th>
                        <th className="border border-black p-1" data-id="element-200">Pulse</th>
                        <th className="border border-black p-1" data-id="element-201">Resp</th>
                        <th className="border border-black p-1" data-id="element-202">BP</th>
                        <th className="border border-black p-1" data-id="element-203">SpO2</th>
                        <th className="border border-black p-1" data-id="element-204">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-205">Height</th>
                        <th className="border border-black p-1" data-id="element-206">BMI</th>
                        <th className="border border-black p-1" data-id="element-207">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-208">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-209">
                      <tr data-id="element-210">
                        <td className="border border-black p-2 font-medium" data-id="element-211">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-212">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-213">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-214">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-215">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-216">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-217">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-218">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-219">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-220">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-221">
                  <div className="border-2 border-black" data-id="element-222">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-223">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-224">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-225">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-226">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-227">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-228">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-229">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-230">
                      <tbody data-id="element-231">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-232">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-233">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-234">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-235">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-236">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-237">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-238">
                      <span className="font-semibold" data-id="element-239">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-240">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-241">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-242">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-243">
                      <tbody data-id="element-244">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-245">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-246">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-247">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-248">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-249">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-250">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-251">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-252">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-253">
                      <tbody data-id="element-254">
                        <tr className="border-b border-gray-300" data-id="element-255">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-256">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-257">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-258">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-259">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-260">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-261">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-262">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-263">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-264">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-265">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-266">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-267">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-268">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-269">
                      <p data-id="element-270">
                        <span className="font-semibold" data-id="element-271">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-272">
                        <span className="font-semibold" data-id="element-273">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-274">
                        <span className="font-semibold" data-id="element-275">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-276">
                        <span className="font-semibold" data-id="element-277">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-278">
                        <span className="font-semibold" data-id="element-279">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-280">
                        <span className="font-semibold" data-id="element-281">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-282">
                        <span className="font-semibold" data-id="element-283">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-284">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-285">
                      On Examination
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-286">
                      {enc.clinicalNote?.onExamination || 'G/C – Stable / Ill Looking / Unconscious\n- Febrile / Afebrile / Lethargic\n- P|J|C\n\nS1  S2 : -\n\nR/S  : -\n\nP/A'}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-287">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-288">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-289">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-290">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-291">{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-292">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-293">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-294">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-295">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-296">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-297">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-298">
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-299">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-300">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-301">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-302">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-303">
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-304">
                        <tbody data-id="element-305">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATINE / U&A', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['LIVER FUNCTION TEST', 'DOA 6'], ['CXR', 'RPR'], ['HIV', 'SCAN/ULTRA SOUND'], ['SPERM ANALYSIS', 'TESTOSTERONE']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-306">
                              {pair.map(test => {
                        const isOrdered = enc.labRequests.some(lab => lab.tests.includes(test)) || test === 'SCAN/ULTRA SOUND' && enc.radiologyRequests.length > 0;
                        return <Fragment key={test} data-id="element-307">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-308">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-309">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-310">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-311">
                        <span className="font-semibold" data-id="element-312">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results — View File list */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-313">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-314">
                            Investigation Results
                          </h4>
                          <table className="w-full border-collapse text-sm" data-id="element-315">
                            <thead data-id="element-316">
                              <tr className="bg-gray-100" data-id="element-317">
                                <th className="border border-black p-1.5 text-left" data-id="element-318">
                                  Test / Scan
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-319">
                                  Type
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-320">
                                  Performed By
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-321">
                                  Completed
                                </th>
                                <th className="border border-black p-1.5 text-center w-32 print:hidden" data-id="element-322">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-323">
                              {enc.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-324">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-325">
                                        {testName}
                                      </td>
                                      <td className="border border-black p-1.5" data-id="element-326">
                                        Laboratory
                                      </td>
                                      <td className="border border-black p-1.5" data-id="element-327">
                                        —
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-328">
                                        {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                      </td>
                                      <td className="border border-black p-1.5 text-center print:hidden" data-id="element-329">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id,
                            testName
                          })} data-id="element-330">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-331" />{' '}
                                          View File
                                        </Button>
                                      </td>
                                    </tr>))}
                              {enc.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-332">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-333">
                                      {lab.otherTests}{' '}
                                      <span className="text-xs text-gray-500" data-id="element-334">
                                        (Other)
                                      </span>
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-335">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-336">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-337">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-338">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id
                          })} data-id="element-339">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-340" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                              {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-341">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-342">
                                      {rad.scanType}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-343">
                                      Radiology
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-344">
                                      {rad.radiographerName || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-345">
                                      {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-346">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            encounterId: enc.id,
                            radId: rad.id
                          })} data-id="element-347">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-348" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-349">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-350">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-351">
                      <thead data-id="element-352">
                        <tr className="bg-gray-100" data-id="element-353">
                          <th className="border border-black p-1.5 w-10" data-id="element-354">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-355">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-356">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-357">Route</th>
                          <th className="border border-black p-1.5" data-id="element-358">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-359">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-360">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-361">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-362">
                                <td className="border border-black p-1.5" data-id="element-363">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-364">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-365">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-366">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-367">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-368">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-369">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-370">
                                <td className="border border-black p-3" data-id="element-371">{i}</td>
                                <td className="border border-black p-3" data-id="element-372"></td>
                                <td className="border border-black p-3" data-id="element-373"></td>
                                <td className="border border-black p-3" data-id="element-374"></td>
                                <td className="border border-black p-3" data-id="element-375"></td>
                                <td className="border border-black p-3" data-id="element-376"></td>
                                <td className="border border-black p-3" data-id="element-377"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-378">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-379">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-380">
                        <thead data-id="element-381">
                          <tr className="bg-gray-100" data-id="element-382">
                            <th className="border border-black p-1.5 w-10" data-id="element-383">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-384">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-385">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-386">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-387">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-388">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-389">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-390">
                              <td className="border border-black p-1.5 text-center" data-id="element-391">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-392">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-393">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-394">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-395">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-396">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-397">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-398">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-399">
                      <thead data-id="element-400">
                        <tr className="bg-gray-100" data-id="element-401">
                          <th className="border border-black p-1 w-10" data-id="element-402">No.</th>
                          <th className="border border-black p-1" data-id="element-403">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-404">No.</th>
                          <th className="border border-black p-1" data-id="element-405">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-406">No.</th>
                          <th className="border border-black p-1" data-id="element-407">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-408">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-409">
                            <td className="border border-black p-1 font-medium" data-id="element-410">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-411">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-412">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-413"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-414">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-415"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-416">
                    <p className="font-bold text-sm" data-id="element-417">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-418">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-419">
              <p className="text-gray-500 text-center py-8" data-id="element-420">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>

        {viewingTest && (() => {
        const enc = encounters.find(e => e.id === viewingTest.encounterId);
        if (!enc) return null;
        const lab = viewingTest.kind === 'lab' ? enc.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? enc.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-421">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-422">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-423">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-424">
                      {lab ? 'Laboratory Result' : 'Radiology Result'}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-425">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-426">
                        <Printer className="w-4 h-4 mr-1" data-id="element-427" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-428">
                        <Download className="w-4 h-4 mr-1" data-id="element-429" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-430">
                        <X className="w-5 h-5 text-gray-500" data-id="element-431" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-432">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-433">
                      <div className="flex items-center" data-id="element-434">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-435" />
                        <div data-id="element-436">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-437">
                            BMED Medical Centre
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-438">
                            Maternity Care Made Better
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-439">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-440">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-441">
                          <span className="font-semibold" data-id="element-442">Patient:</span>{' '}
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p data-id="element-443">
                          <span className="font-semibold" data-id="element-444">File No:</span>{' '}
                          {selectedPatient.bmcFileNumber}
                        </p>
                        <p data-id="element-445">
                          <span className="font-semibold" data-id="element-446">Age/Sex:</span>{' '}
                          {selectedPatient.age} / {selectedPatient.sex}
                        </p>
                        <p data-id="element-447">
                          <span className="font-semibold" data-id="element-448">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {lab && (() => {
                const focusedTest = viewingTest?.kind === 'lab' ? viewingTest.testName : undefined;
                const showFBC = focusedTest && isFBCTest(focusedTest);
                const showUA = focusedTest && isUrinalysisTest(focusedTest);
                const showT = focusedTest && isTestosteroneTest(focusedTest);
                return <div data-id="element-449">
                            <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-450">
                              {focusedTest || 'Tests Performed'}
                            </h2>
                            {showFBC ? <div className="space-y-4" data-id="element-451">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-452">
                                  <h3 className="font-bold uppercase" data-id="element-453">
                                    Full Blood Count (FBC) Report
                                  </h3>
                                </div>
                                {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-454">
                                    <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-455">
                                      {cat.category}
                                    </div>
                                    <table className="w-full border-collapse text-sm" data-id="element-456">
                                      <thead data-id="element-457">
                                        <tr className="bg-gray-50" data-id="element-458">
                                          <th className="border border-black p-1.5 text-left" data-id="element-459">
                                            Test
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-460">
                                            Result
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-461">
                                            Reference Range
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-24" data-id="element-462">
                                            Unit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-463">
                                        {cat.tests.map(t => <tr key={t.name} data-id="element-464">
                                            <td className="border border-black p-1.5" data-id="element-465">
                                              {t.name}
                                            </td>
                                            <td className="border border-black p-1.5 font-bold" data-id="element-466">
                                              {lab.results && lab.results[t.name] || '—'}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-467">
                                              {t.range}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-468">
                                              {t.unit}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>)}
                              </div> : showUA ? <div data-id="element-469">
                                <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-470">
                                  <h3 className="font-bold uppercase" data-id="element-471">
                                    Urinalysis Report
                                  </h3>
                                </div>
                                <div className="max-w-md mx-auto space-y-2" data-id="element-472">
                                  {urinalysisTests.map(t => {
                        const key = `UA_${t.name}`;
                        const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                        return <div key={t.name} className="flex items-center text-sm" data-id="element-473">
                                        <span className="w-16 font-bold" data-id="element-474">
                                          {t.name}
                                        </span>
                                        <span className="mx-3 text-gray-400" data-id="element-475">
                                          -
                                        </span>
                                        <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-476">
                                          {value || '\u00A0'}
                                        </span>
                                      </div>;
                      })}
                                </div>
                                <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-477">
                                  <div data-id="element-478">
                                    <p className="font-semibold mb-2" data-id="element-479">
                                      Dr.'s Name:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-480">
                                      {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                    </p>
                                  </div>
                                  <div data-id="element-481">
                                    <p className="font-semibold mb-2" data-id="element-482">
                                      Signature:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-483">
                                      {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                    </p>
                                  </div>
                                </div>
                              </div> : showT ? <div className="space-y-2" data-id="element-484">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-485">
                                  <h3 className="font-bold uppercase" data-id="element-486">
                                    Testosterone Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-487">
                                  <thead data-id="element-488">
                                    <tr className="bg-gray-100" data-id="element-489">
                                      <th className="border border-black p-1.5 text-left" data-id="element-490">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-491">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-492">
                                        Reference (Male)
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-493">
                                        Reference (Female)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-494">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-495">
                                    {testosteroneTests.map(t => <tr key={t.name} data-id="element-496">
                                        <td className="border border-black p-1.5" data-id="element-497">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-498">
                                          {lab.results && lab.results[`TESTO_${t.name}`] || lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-499">
                                          {t.rangeMale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-500">
                                          {t.rangeFemale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-501">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div> : <>
                                <p className="mb-4 text-sm" data-id="element-502">
                                  <span className="font-semibold" data-id="element-503">
                                    Tests Ordered:
                                  </span>{' '}
                                  {lab.tests.join(', ')}
                                  {lab.otherTests ? `, ${lab.otherTests}` : ''}
                                </p>
                                {lab.results && Object.keys(lab.results).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-504">
                                    <thead data-id="element-505">
                                      <tr className="bg-gray-100" data-id="element-506">
                                        <th className="border border-black p-2 text-left" data-id="element-507">
                                          Parameter
                                        </th>
                                        <th className="border border-black p-2 text-left" data-id="element-508">
                                          Result
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-509">
                                      {Object.entries(lab.results).map(([k, v]) => <tr key={k} data-id="element-510">
                                            <td className="border border-black p-2 font-medium" data-id="element-511">
                                              {k}
                                            </td>
                                            <td className="border border-black p-2" data-id="element-512">
                                              {v}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table> : <p className="text-gray-500 italic" data-id="element-513">
                                    No structured results recorded.
                                  </p>}
                              </>}
                          </div>;
              })()}

                    {rad && <div data-id="element-514">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-515">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-516">
                            <span className="font-semibold" data-id="element-517">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-518">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-519">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-520">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-521">
                              <h3 className="font-bold" data-id="element-522">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-523">
                                <button onClick={() => setImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-524">
                                  <ZoomOut className="w-4 h-4" data-id="element-525" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className="w-32" data-id="element-526" />
                                <button onClick={() => setImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-527">
                                  <ZoomIn className="w-4 h-4" data-id="element-528" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-529">
                                  {imageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-530">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-531">
                                  <div style={{
                        transform: `scale(${imageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-532">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-533" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-534">
      <div className="flex justify-between items-center" data-id="element-535">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-536">Patient Directory</h1>
      </div>

      <Card data-id="element-537">
        <div className="relative" data-id="element-538">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-539">
            <Search className="h-5 w-5 text-gray-400" data-id="element-540" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-541" />
        </div>
      </Card>

      <Card noPadding data-id="element-542">
        <div className="overflow-x-auto" data-id="element-543">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-544">
            <thead className="bg-gray-50" data-id="element-545">
              <tr data-id="element-546">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-547">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-548">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-549">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-550">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-551">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-552">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-553">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-554">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-555">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-556">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-557">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-558">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-559">
                        <FileText className="w-4 h-4 mr-2" data-id="element-560" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-561">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-562">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};